import Utils from './Utils';
import {
    allReferencedPathsExistInSource,
    conditionLhsForWorkflow,
    evaluateWorkflowContextExpression,
    workflowContextExpressionLooksLikeExpression,
} from './workflowContextExpression';

function conditionValueEquals(left, right) {
    try {
        return JSON.stringify(left) === JSON.stringify(right);
    } catch (_err) {
        return false;
    }
}

function parseListOperand(raw) {
    if (raw == null || raw === '')
        return [];
    if (Array.isArray(raw))
        return raw;
    const s = String(raw).trim();
    if (!s)
        return [];
    try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed))
            return parsed;
    } catch (_err) {
        /* ignore */
    }
    return s.split(',').map((x) => x.trim()).filter(Boolean);
}

function compareScalar(left, right, op) {
    let cmp = 0;
    if (Number.isFinite(Number(left)) && Number.isFinite(Number(right))) {
        const l = Number(left);
        const r = Number(right);
        cmp = l === r ? 0 : (l > r ? 1 : -1);
    } else {
        cmp = String(left).localeCompare(String(right));
    }
    if (op === 'gt')
        return cmp > 0;
    if (op === 'lt')
        return cmp < 0;
    if (op === 'gte')
        return cmp >= 0;
    if (op === 'lte')
        return cmp <= 0;
    return false;
}

/**
 * @param {object} source workflow / form validation source
 * @param {{ path?: string, op?: string, value?: unknown }} condition
 * @returns {boolean}
 */
export function evaluateSubmitCondition(source, condition) {
    const path = String(condition?.path || '').trim();
    const op = String(condition?.op || '').trim();
    if (!path || !op)
        return false;

    const absent = '__absent__';
    const value = condition?.value;

    if (op === 'eq')
        return conditionValueEquals(evaluateWorkflowContextExpression(source, path), value);
    if (op === 'neq')
        return !conditionValueEquals(evaluateWorkflowContextExpression(source, path), value);
    if (op === 'exists') {
        if (workflowContextExpressionLooksLikeExpression(path))
            return allReferencedPathsExistInSource(source, path);
        return Utils.getByPath(source, path) !== undefined;
    }
    if (op === 'not_exists') {
        if (workflowContextExpressionLooksLikeExpression(path))
            return !allReferencedPathsExistInSource(source, path);
        return Utils.getByPath(source, path) === undefined;
    }
    if (op === 'truthy')
        return Boolean(evaluateWorkflowContextExpression(source, path));

    const got = conditionLhsForWorkflow(source, path);
    if (op === 'is_true')
        return got !== absent && got === true;
    if (op === 'is_false')
        return got !== absent && got === false;

    if (['gt', 'gte', 'lt', 'lte'].includes(op)) {
        if (got === absent)
            return false;
        return compareScalar(got, value, op);
    }
    if (op === 'in_list' || op === 'not_in_list') {
        if (got === absent)
            return false;
        const list = parseListOperand(value);
        const inList = list.some((item) => conditionValueEquals(item, got) || String(item) === String(got));
        return op === 'in_list' ? inList : !inList;
    }

    return false;
}

/**
 * @param {{ operator?: string, expressions?: object[] }|null|undefined} validation
 * @param {object} source
 * @returns {boolean}
 */
export function isSubmitValidationOk(validation, source) {
    const expressions = Array.isArray(validation?.expressions) ? validation.expressions : [];
    const validExpressions = expressions.filter((e) => String(e?.path || '').trim() && String(e?.op || '').trim());
    if (validExpressions.length === 0)
        return true;

    const operator = String(validation?.operator || 'and').toLowerCase();
    if (operator === 'or')
        return validExpressions.some((expression) => evaluateSubmitCondition(source, expression));
    return validExpressions.every((expression) => evaluateSubmitCondition(source, expression));
}
